import { useEffect, useState, type ComponentType } from "react";

import type { ChartProps } from "./chartTypes";
import { InlineError, Spinner } from "./Feedback";

export type {
  ChartDatum,
  ChartDonutProps,
  ChartProps,
  ChartSegment,
  ChartSimpleProps,
  ChartSize,
  ChartStackedBarProps,
  ChartStackedDatum,
  ChartVariant,
} from "./chartTypes";

type ChartCanvasComponent = ComponentType<ChartProps>;

function classNames(classes: string[], className: string | undefined): string {
  if (className) {
    classes.push(className);
  }

  return classes.join(" ");
}

function chartErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Chart unavailable.";
}

export function Chart(props: ChartProps) {
  const [Canvas, setCanvas] = useState<ChartCanvasComponent | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    // @visx/shape is funnelled through a single dynamic import so it (and the
    // tree-shaken d3-shape chain it pulls in) lands in its own lazy chunk
    // (mirrors the AudioPlayer/wavesurfer, CodeEditor/codeEditorModule, and
    // Markdown/markdownModule precedents) rather than the always-loaded main
    // app bundle — keeping the barrel re-export and the StatCard sparkline path
    // free of static visx value imports.
    void import("./chartModule")
      .then((module) => {
        if (!cancelled) {
          setCanvas(() => module.ChartCanvas);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setLoadError(chartErrorMessage(error));
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (Canvas) {
    return <Canvas {...props} />;
  }

  const classes = classNames(
    [
      "marker-chart",
      `marker-chart--${props.variant}`,
      `marker-chart--${props.size ?? "default"}`,
    ],
    props.className,
  );

  return (
    <div aria-busy={!loadError} className={classes}>
      {loadError ? (
        <InlineError>{loadError}</InlineError>
      ) : (
        <span className="marker-chart__loading">
          <Spinner label="Loading chart" />
        </span>
      )}
    </div>
  );
}
